import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import { reduxForm, Field, FieldArray } from 'redux-form';
import { Ref, Grid, Button, Icon, Popup, Checkbox } from 'semantic-ui-react';
import { isNil, get } from 'lodash';

import * as Selectors from 'Pages/manageingredient/state/editingredient/selectors';
import { InputField, Select } from 'Common/SemanticWrapper';
import { ING_MEASUREMENTS_FORM_NAME, InputMode } from 'Common/FormEnum';
import { IngMeasurementsFieldEnum as Fields } from 'Common/FieldEnum';
import {
  WEIGHT_UNIT_OPTIONS,
  WEIGHT_UNITS,
} from 'Pages/manageingredient/helpers/Constants';
import { scrollOnSubmitFail } from 'Common/FormHelper';
import {
  computeCostPerServing,
  convertToGrams,
} from 'Pages/manageingredient/helpers/ingredient';
import { setNotificationDialog } from 'Components/dialogs/state/actions';
import { NOTIFICATION_DIALOG_MESSAGES } from 'Common/Constants';

class MeasuresAndCostForm extends Component {
  gridRef = React.createRef();

  componentDidUpdate(prevProps) {
    const { measures: prevMeasures } = prevProps;
    const { measures } = this.props;

    if (prevMeasures !== measures && measures.length > prevMeasures.length) {
      if (this.gridRef.current) {
        const gridEl = this.gridRef.current;
        const scrollHeight = gridEl.scrollHeight;
        const containerHeight = gridEl.clientHeight;

        // Scroll to where the new input was just added
        gridEl.scrollTo({
          left: 0,
          top: scrollHeight - containerHeight,
          behavior: 'smooth',
        });
      }
    }
  }

  addInputRow = () => {
    const {
      array: { push },
      measures,
    } = this.props;

    if (measures.length > 0) {
      push(Fields.Measures, {
        [Fields.Amount]: '',
        [Fields.Measurement]: '',
        [Fields.Weight]: '',
        [Fields.BaseMeasure]: WEIGHT_UNITS.GRAMS,
      });
    } else {
      push(Fields.Measures, {
        [Fields.Amount]: '',
        [Fields.Measuremenet]: '',
        [Fields.Weight]: '',
        [Fields.BaseMeasure]: WEIGHT_UNITS.GRAMS,
        [Fields.IsDefault]: true,
      });
    }
  };

  renderCostPerServing = (index) => ({ input }) => {
    const { measures, costPerGram } = this.props;
    const { value } = input;

    const measure = measures[index];

    const weight = get(measure, Fields.Weight, 0);
    const measureUnit = get(measure, Fields.BaseMeasure, WEIGHT_UNITS.GRAMS);
    const weightInGrams = convertToGrams(weight, measureUnit);
    const costPerServing = computeCostPerServing(costPerGram, weightInGrams);

    if (!isNil(value) && !isNaN(costPerServing)) {
      return `$${costPerServing}`;
    }
    return null;
  };

  setMeasureAsDefault = (index) => () => {
    const { change, measures } = this.props;

    const newMeasures = measures.map((m) => ({
      ...m,
      [Fields.IsDefault]: false,
    }));

    newMeasures[index][Fields.IsDefault] = true;

    change(Fields.Measures, newMeasures);
  };

  removeMeasure = ({ fieldArray, index, isDefault }) => () => {
    const { isReadOnly, measures, change, setNotificationDialog } = this.props;

    if (!isReadOnly) {
      if (!isDefault) {
        setNotificationDialog({
          ...NOTIFICATION_DIALOG_MESSAGES.DELETE_INGREDIENT_MEASURE,
          functionsToRunOnOk: [
            () => {
              fieldArray.remove(index);
            },
          ],
        });
      } else {
        let newMeasures = [...measures];
        newMeasures.splice(index, 1);

        if (newMeasures.length > 0) {
          const newDefaultIndex =
            index >= newMeasures.length ? index - 1 : index;
          newMeasures[newDefaultIndex][Fields.IsDefault] = true;
        }

        setNotificationDialog({
          ...NOTIFICATION_DIALOG_MESSAGES.DELETE_INGREDIENT_MEASURE,
          functionsToRunOnOk: [
            () => {
              change(Fields.Measures, newMeasures);
            },
          ],
        });
      }
    }
  };

  renderInputRows = ({ fields }) => {
    const { isReadOnly } = this.props;

    if (fields.length > 0) {
      return (
        <Fragment>
          <Grid.Row className="weights-measures-labels">
            <Grid.Column textAlign="center" width={2}>
              Amount
            </Grid.Column>
            <Grid.Column textAlign="center" width={3}>
              Measure Unit
            </Grid.Column>
            <Grid.Column width={1} />
            <Grid.Column textAlign="center" width={2}>
              Weight
            </Grid.Column>
            <Grid.Column textAlign="center" width={3}>
              Weight Unit
            </Grid.Column>
            <Grid.Column textAlign="center" width={2}>
              Cost Per Serving
            </Grid.Column>
            <Grid.Column textAlign="center" width={2}>
              Default
            </Grid.Column>
            {!isReadOnly && <Grid.Column width={1} />}
          </Grid.Row>
          {fields.map((member, index, fieldArray) => {
            const measure = fields.get(index);
            const isDefault = measure[Fields.IsDefault];
            const usedInRecipe = measure[Fields.UsedInRecipe];

            const disabled = isReadOnly || usedInRecipe;

            return (
              <Grid.Row key={index} style={{ minHeight: 92 }}>
                <Grid.Column width={2}>
                  <Field
                    name={`${member}.${Fields.Amount}`}
                    component={InputField}
                    fluid
                    mode={InputMode.Decimal}
                    disabled={disabled}
                    numericSize={[10, 2]}
                    label={false}
                    type="text"
                  />
                </Grid.Column>
                <Grid.Column width={3}>
                  <Field
                    name={`${member}.${Fields.Measurement}`}
                    component={InputField}
                    fluid
                    disabled={disabled}
                    maxLength="120"
                    label={false}
                    type="text"
                  />
                </Grid.Column>
                <Grid.Column
                  textAlign="center"
                  width={1}
                  className="weights-measures-equal"
                >
                  =
                </Grid.Column>
                <Grid.Column width={2}>
                  <Field
                    name={`${member}.${Fields.Weight}`}
                    component={InputField}
                    fluid
                    mode={InputMode.Decimal}
                    disabled={disabled}
                    numericSize={[10, 2]}
                    label={false}
                    type="text"
                  />
                </Grid.Column>
                <Grid.Column width={3}>
                  <Field
                    name={`${member}.${Fields.BaseMeasure}`}
                    component={Select}
                    fluid
                    usePortal
                    options={WEIGHT_UNIT_OPTIONS}
                    disabled={disabled}
                  />
                </Grid.Column>
                <Grid.Column
                  width={2}
                  textAlign="center"
                  className="weights-measures-cps"
                >
                  <Field
                    name={`${member}.${Fields.CostPerServing}`}
                    component={this.renderCostPerServing(index)}
                  />
                </Grid.Column>
                <Grid.Column width={2} textAlign="center">
                  <Checkbox
                    disabled={isDefault || disabled}
                    onChange={this.setMeasureAsDefault(index)}
                    checked={isDefault}
                    className="weights-measures-modal-checkbox"
                  />
                </Grid.Column>
                {!isReadOnly && (
                  <Grid.Column width={1} textAlign="center">
                    <Popup
                      content="Delete"
                      trigger={
                        <Icon
                          className="trash weights-measures-modal-icon"
                          size="large"
                          onClick={this.removeMeasure({
                            fieldArray,
                            index,
                            isDefault,
                          })}
                          disabled={disabled}
                        />
                      }
                      style={{ zIndex: 10000 }}
                    />
                  </Grid.Column>
                )}
              </Grid.Row>
            );
          })}
        </Fragment>
      );
    }
    return <div>No measurement data to display.</div>;
  };

  render() {
    const { isReadOnly } = this.props;

    const showAddButton = !isReadOnly;

    return (
      <Fragment>
        <Ref innerRef={this.gridRef}>
          <Grid id="weights-measures-grid">
            <FieldArray
              component={this.renderInputRows}
              name={Fields.Measures}
            />
          </Grid>
        </Ref>
        {showAddButton && (
          <Grid>
            <Grid.Row>
              <Grid.Column>
                <Button type="button" onClick={this.addInputRow}>
                  Add New
                </Button>
              </Grid.Column>
            </Grid.Row>
          </Grid>
        )}
      </Fragment>
    );
  }
}

const validate = (values) => {
  const errors = {};

  const measures = values[Fields.Measures];

  if (measures.length) {
    const measureErrorList = [];

    measures.forEach((measure, i) => {
      const measureError = {};

      if (!measure[Fields.Amount]) {
        measureError[Fields.Amount] = 'Required';
      }

      if (!measure[Fields.Measurement]) {
        measureError[Fields.Measurement] = 'Required';
      }

      if (!measure[Fields.Weight]) {
        measureError[Fields.Weight] = 'Required';
      }

      if (!measure[Fields.BaseMeasure]) {
        measureError[Fields.BaseMeasure] = 'Required';
      }

      measureErrorList[i] = measureError;
    });

    errors[Fields.Measures] = measureErrorList;
  }

  return errors;
};

const mapStateToProps = (state) => ({
  measures: Selectors.getFormMeasures(state),
  costPerGram: Selectors.getCostPerGram(state),
});

const mapDispatchToProps = { setNotificationDialog };

MeasuresAndCostForm = connect(
  mapStateToProps,
  mapDispatchToProps
)(
  reduxForm({
    form: ING_MEASUREMENTS_FORM_NAME,
    destroyOnUnmount: false,
    validate,
    onSubmitFail: scrollOnSubmitFail('weights-measures-grid'),
  })(MeasuresAndCostForm)
);

export { MeasuresAndCostForm };



// WEBPACK FOOTER //
// ./src/pages/manageingredient/subpages/editingredient/components/MeasuresAndCostForm.js